import { Button as ButtonV2 } from "../../Button";
import { Datepicker } from "../../Datepicker";
import { FilterIcon } from "../../Icons";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportContext } from "../providers/PerformanceReportProvider";
import {
  PERFORMANCE_REPORT_MAX_RANGE_DAYS,
  PERFORMANCE_REPORT_MIN_DATE,
} from "../urlParams/schema";

type PerformanceControlsProps = {
  /** Opens the filter modal (owned as local state by the page). */
  onOpenFilter: () => void;
};

export const PerformanceControls = ({
  onOpenFilter,
}: PerformanceControlsProps) => {
  const translations = usePerformanceReportTranslations();
  const { dateRange, setDateRange, resetDateRange, filterCount } =
    usePerformanceReportContext();

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div data-testid="performance-date-picker">
        <TooltipWrapper
          color="blue"
          tooltipPlacement="top"
          tooltipWidth={288}
          text={translations.dateRangeLimitation}
        >
          <Datepicker
            value={dateRange}
            inputId="performanceDateRange"
            displayVariant="field"
            minDate={PERFORMANCE_REPORT_MIN_DATE}
            limitDays={PERFORMANCE_REPORT_MAX_RANGE_DAYS}
            onChange={(value) => {
              if (!value?.startDate || !value?.endDate) {
                resetDateRange();
                return;
              }
              setDateRange({
                startDate: value.startDate,
                endDate: value.endDate,
              });
            }}
            configs={{
              shortcuts: {
                today: translations.datepickerToday,
                yesterday: translations.datepickerYesterday,
                past: translations.datepickerPast,
                currentMonth: translations.datepickerCurrentMonth,
                pastMonth: translations.datepickerPastMonth,
              },
              footer: {
                cancel: translations.datepickerCancel,
                apply: translations.datepickerApply,
              },
            }}
            allowClear={false}
          />
        </TooltipWrapper>
      </div>
      <ButtonV2 variant="tonal" size="md" onClick={onOpenFilter}>
        <span className="flex items-center gap-2">
          <FilterIcon
            className="h-4 w-4"
            aria-hidden="true"
            focusable="false"
          />
          <Typo element="span" type="labelSM">
            {translations.filterButton}
          </Typo>
          {filterCount > 0 && (
            <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-brand-default px-1.5">
              <Typo
                element="span"
                type="label2XS"
                textColor="text-text-inverse-primary"
              >
                {filterCount}
              </Typo>
            </span>
          )}
        </span>
      </ButtonV2>
    </div>
  );
};
